import React, { useState, useEffect, useMemo } from "react";
import { 
  Sparkles, 
  Clock, 
  ArrowRight, 
  Check, 
  X, 
  ShieldAlert, 
  Play, 
  Flame, 
  Layers, 
  ChevronDown, 
  ChevronUp, 
  Star, 
  CheckCircle2, 
  Percent, 
  AlertCircle,
  Award,
  Sparkle
} from "lucide-react";
import { Toaster, toast } from "sonner";
import "@/App.css";

const originalPrices = {
  standard: 5999,
  premium: 8999
};

const promoDiscounts = {
  "SELFHEAL50": 0.50, // 50% off -> standard: ₹2,999, premium: ₹4,499
  "HEALMYSKIN": 0.60,  // 60% off -> standard: ₹2,399, premium: ₹3,599
};

function App() {
  // --- Urgency Countdown Timer ---
  const [timeLeft, setTimeLeft] = useState({ hours: 2, minutes: 14, seconds: 30 });
  
  useEffect(() => {
    // Set or get target end time from localStorage
    const savedTarget = localStorage.getItem("psoriasis_timer_target");
    let targetTime;
    
    if (savedTarget) {
      targetTime = parseInt(savedTarget, 10);
      // If the saved time has expired, reset it to a fresh 2h 14m countdown
      if (targetTime < Date.now()) {
        targetTime = Date.now() + 2 * 3600 * 1000 + 14 * 60 * 1000 + 30 * 1000;
        localStorage.setItem("psoriasis_timer_target", targetTime.toString());
      }
    } else {
      targetTime = Date.now() + 2 * 3600 * 1000 + 14 * 60 * 1000 + 30 * 1000;
      localStorage.setItem("psoriasis_timer_target", targetTime.toString());
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = targetTime - now;

      if (diff <= 0) {
        // Reset timer loop for ongoing urgency
        const nextTarget = Date.now() + 2 * 3600 * 1000 + 14 * 60 * 1000 + 30 * 1000;
        localStorage.setItem("psoriasis_timer_target", nextTarget.toString());
      } else {
        const h = Math.floor(diff / (3600 * 1000));
        const m = Math.floor((diff % (3600 * 1000)) / (60 * 1000));
        const s = Math.floor((diff % (60 * 1000)) / 1000);
        setTimeLeft({ hours: h, minutes: m, seconds: s });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // --- Sticky Navigation and Bottom Bar ---
  const [scrolled, setScrolled] = useState(false);
  const [showBottomBar, setShowBottomBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      if (window.scrollY > 400) {
        setShowBottomBar(true);
      } else {
        setShowBottomBar(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // --- Interactive 21-Day Module Roadmap ---
  const [activeWeek, setActiveWeek] = useState(1);
  const [expandedDay, setExpandedDay] = useState(null);

  const roadmapWeeks = [
    {
      id: 1,
      title: "Week 1: Diagnostics, Triggers & Healing Diets",
      description: "Identify your individual gut-skin triggers, eliminate inflammatory flare-promoters, and lay down the dietary pillars of psoriasis recovery.",
      days: [
        { num: 1, title: "Uncovering the Root Cause", duration: "Day 1", desc: "Understand how gut permeability and liver workload drive cutaneous expression. Complete your baseline inflammation journal." },
        { num: 2, title: "The Psoriasis Trigger Audit", duration: "Day 2", desc: "Identify personal lifestyle, chemical, and dietary triggers. Learn the exact process to audit your environment." },
        { num: 3, title: "Elimination Protocol Launch", duration: "Day 3", desc: "Implement the 14-day anti-inflammatory diet. Remove Nightshades, Gluten, Dairy, and Refined Sugars with premium, tasty alternatives." },
        { num: 4, title: "Supercharging Gut Healing Foods", duration: "Day 4", desc: "Integrate bone broths (or vegan collagen-boosters), fermented foods, and glutamine-rich ingredients to seal the intestinal tight junctions." },
        { num: 5, title: "Liver Pathways Support", duration: "Day 5", desc: "Support Phase I and Phase II liver detoxification using cruciferous complexes, milk thistle protocols, and warm lemon hydration routines." },
        { num: 6, title: "Hydration & Mineral Balance", duration: "Day 6", desc: "Balance cellular water intake with key electrolytes (Magnesium, Potassium) to soothe irritated epidermal membranes from the inside." },
        { num: 7, title: "Week 1 Review & Adaptation", duration: "Day 7", desc: "Evaluate skin symptom responses. Adjust macro-nutrient ratios and prepare for systemic detox in Week 2." }
      ]
    },
    {
      id: 2,
      title: "Week 2: Gut-Skin Optimization & Deep Detox",
      description: "Flush circulating endotoxins, balance your microbiome, and support your lymphatic system to accelerate cellular skin renewal.",
      days: [
        { num: 8, title: "Microbiome Rebalancing", duration: "Day 8", desc: "Target harmful opportunistic bacteria. Introduce herbal anti-microbials safely without stripping beneficial flora." },
        { num: 9, title: "Lymphatic Flow Activation", duration: "Day 9", desc: "Learn dry brushing sequences, contrast showering protocols, and gentle movement exercises to drain stagnant wastes." },
        { num: 10, title: "The Healing Sleep Protocol", duration: "Day 10", desc: "Address nighttime itching and insomnia. Establish a low-EMF, dark-room sleep environment that promotes pineal melatonin secretion." },
        { num: 11, title: "Stress-Skin Vagus Stimulation", duration: "Day 11", desc: "Use deep somatic breathwork (4-7-8 method) and vagus nerve toning to switch your nervous system from sympathetic (flare-up state) to parasympathetic." },
        { num: 12, title: "Natural Topical Therapy", duration: "Day 12", desc: "Safe, non-steroidal herbal topical recipes. Use pure Aloe Vera, Virgin Coconut Oil, Neem, and Tea Tree blends to soothe active plaques." },
        { num: 13, title: "Sunlight & Vitamin D3 Pathways", duration: "Day 13", desc: "How to leverage smart, non-burning UVB sun exposure and oral D3/K2 supplementation to down-regulate hyper-active T-cells." },
        { num: 14, title: "Week 2 Detox Cleanse Checklist", duration: "Day 14", desc: "Evaluate gut movement regularity. Establish clean elimination habits essential for clearing skin plaques." }
      ]
    },
    {
      id: 3,
      title: "Week 3: Long-term Remission & Maintenance",
      description: "Establish a resilient skin barrier, systematically reintroduce foods, and lock in lifetime habits to keep your skin clear forever.",
      days: [
        { num: 15, title: "The Systematic Food Reintroduction", duration: "Day 15", desc: "Begin testing eliminated foods safely. Learn to read subtle biological responses (pulse spike, mild itch) to log trigger reactions." },
        { num: 16, title: "Intestinal Barrier Reinforcement", duration: "Day 16", desc: "Seal your gut permanently using Zinc Carnosine, Slippery Elm bark infusion, and high-potency targeted spore probiotics." },
        { num: 17, title: "Adrenal & Cortisol Balancing", duration: "Day 17", desc: "Stabilize morning cortisol spikes. Use adaptogenic herbs (Ashwagandha, Holy Basil) to prevent stress-induced skin flares." },
        { num: 18, title: "Heavy Metal & Environmental Purge", duration: "Day 18", desc: "Filter toxic loads from tap water, cookware, and skincare. Switch to clean household and personal care standards." },
        { num: 19, title: "Developing Epidermal Resilience", duration: "Day 19", desc: "Soothe scars and post-inflammatory hyperpigmentation. Use Rosehip Seed oil and sea buckthorn to repair and regenerate damaged dermal tissue." },
        { num: 20, title: "The Lifetime Remission Playbook", duration: "Day 20", desc: "Build your customized '80/20 rule' guide. How to navigate dining out, travel, and social events without triggering a regression." },
        { num: 21, title: "21-Day Graduation & Roadmap", duration: "Day 21", desc: "Transition into self-healing maintenance. Outline your next 14 days of the 35-day access portal to master the self-heal process." }
      ]
    }
  ];

  // --- Interactive Self-Heal Assessment Calculator ---
  const [assessmentStep, setAssessmentStep] = useState(1);
  const [assessmentAnswers, setAssessmentAnswers] = useState({
    type: "",
    duration: "",
    intensity: ""
  });
  const [calculatedPath, setCalculatedPath] = useState(null);

  const handleSelectOption = (key, value) => {
    const updatedAnswers = { ...assessmentAnswers, [key]: value };
    setAssessmentAnswers(updatedAnswers);
    
    if (key === "type") {
      setAssessmentStep(2);
    } else if (key === "duration") {
      setAssessmentStep(3);
    } else if (key === "intensity") {
      // Calculate path based on answers
      let stage = "Moderate Plaque Flare";
      let recoveryProbability = "88%";
      let recommendation = "Focus on Phase I liver detox, targeted nightshade elimination, and hydration mineral balance.";
      let durationNeeded = "Full 21 Days Core Reset Protocol";

      const isSevere = updatedAnswers.type === "erythrodermic" || value === "intense" || updatedAnswers.duration === "3-years-plus";
      const isMild = updatedAnswers.type === "scalp" && value === "mild" && updatedAnswers.duration === "under-6-months";

      if (isSevere) {
        stage = "Deep Systemic Inflammatory Flare";
        recoveryProbability = "94%";
        recommendation = "Prioritize gut tight-junction sealing (slippery elm + spore probiotics) and deep Phase II hepatic conjugation.";
        durationNeeded = "Full 21 Days Core Protocol + 14 Days Extended Barrier Sealing";
      } else if (isMild) {
        stage = "Early Stage Acute Scalp Flare";
        recoveryProbability = "82%";
        recommendation = "Targeted scalp oil nutrition sequences and rapid 7-day digestive cleanse.";
        durationNeeded = "14 Days Diet Reset + 7 Days Topical Botanical Repair";
      }

      setCalculatedPath({
        stage,
        recoveryProbability,
        recommendation,
        durationNeeded,
        answers: updatedAnswers
      });
      setAssessmentStep(4);
    }
  };

  const handleResetAssessment = () => {
    setAssessmentAnswers({ type: "", duration: "", intensity: "" });
    setAssessmentStep(1);
    setCalculatedPath(null);
  };

  // --- Interactive Checkout Modal State ---
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("standard"); // standard vs premium
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPlanPromo] = useState("");
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  
  // Credit Card fields
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCVV] = useState("");

  const planDiscountedPrice = (plan) => {
    const base = originalPrices[plan];
    const discount = appliedPromo && promoDiscounts[appliedPromo] ? promoDiscounts[appliedPromo] : 0.50;
    
    // Explicit fixed pricing mapping to avoid any floating-point or rounding inconsistencies
    if (discount === 0.50) {
      return plan === "standard" ? 2999 : 4499;
    }
    if (discount === 0.60) {
      return plan === "standard" ? 2399 : 3599;
    }
    return Math.floor(base * (1 - discount));
  };

  const currentPrice = useMemo(() => {
    return planDiscountedPrice(selectedPlan);
  }, [selectedPlan, appliedPromo]);

  const gstAmount = useMemo(() => {
    return Math.round(currentPrice * 0.18 * 100) / 100;
  }, [currentPrice]);

  const totalDueAmount = useMemo(() => {
    return Math.round((currentPrice + gstAmount) * 100) / 100;
  }, [currentPrice, gstAmount]);

  const discountAmount = useMemo(() => {
    const base = originalPrices[selectedPlan];
    return base - currentPrice;
  }, [selectedPlan, currentPrice]);

  useEffect(() => {
    // Prefill SELFHEAL50 by default as the general promo
    setAppliedPlanPromo("SELFHEAL50");
  }, []);

  const handleApplyPromo = (e) => {
    e.preventDefault();
    const code = promoCode.trim().toUpperCase();
    if (promoDiscounts[code]) {
      setAppliedPlanPromo(code);
      toast.success(`Success! Code ${code} applied successfully!`);
    } else {
      toast.error("Invalid coupon code. Try 'SELFHEAL50' or take the skin assessment quiz!");
    }
  };

  const handleOpenCheckout = (plan = "standard") => {
    setSelectedPlan(plan);
    setCardName("");
    setCardNumber("");
    setCardExpiry("");
    setCardCVV("");
    setCheckoutOpen(true);
    setOrderConfirmed(false);
  };

  const handleUnlockCustomDiscount = () => {
    setAppliedPlanPromo("HEALMYSKIN");
    setPromoCode("HEALMYSKIN");
    setSelectedPlan("standard");
    setCardName("");
    setCardNumber("");
    setCardExpiry("");
    setCardCVV("");
    setCheckoutOpen(true);
    setOrderConfirmed(false);
    toast.success("Quiz completed! Custom 60% OFF coupon 'HEALMYSKIN' applied!");
  };

  const handleCheckoutSubmit = (e) => {
    e.preventDefault();
    if (!cardName || !cardNumber || !cardExpiry || !cardCvv) {
      toast.error("Please fill in all credit card fields.");
      return;
    }
    
    setIsSubmittingOrder(true);
    setTimeout(() => {
      setIsSubmittingOrder(false);
      setOrderConfirmed(true);
      toast.success("Order Placed Successfully!");
    }, 1500);
  };

  // --- Escape Key Modal Closing Logic ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && checkoutOpen) {
        setCheckoutOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [checkoutOpen]);

  // --- FAQs State ---
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqItems = [
    {
      q: "How does the Self-Heal Process differ from the main Second Brain Coach program?",
      a: "The curriculum, tools, worksheets, diet lists, and recipes are 100% identical. The difference is delivery: there are no live coaching webinars, direct Slack messaging, or 1-on-1 nutritional consultations with the founder. It is designed for self-motivated creators who want to follow the scientific path independently at a fraction of the cost."
    },
    {
      q: "Why is portal access strictly limited to 35 days?",
      a: "Psoriasis recovery requires high focus and immediate action. We have learned that indefinite lifetime access breeds procrastination. By limiting portal access to 35 days, we provide a healthy boundary: you have 21 days for the core process, plus 14 extra buffer days to review and download your life-long guides. This boundary dramatically improves program completion and success rates."
    },
    {
      q: "Can I upgrade to the full main program later?",
      a: "Yes, but with a condition: you can upgrade and credit your Self-Heal fees towards the main program ONLY within the first week of enrollment. After the first week, this credit option expires and you will have to pay the full Guided Program registration fees if you wish to join later."
    },
    {
      q: "Is this protocol safe? Are there any medical side-effects?",
      a: "Absolutely. Our process is 100% holistic, natural, and safe. There are NO pharmaceutical medicines and NO steroid creams of any kind given or prescribed in this program. We focus entirely on cooling system inflammation, sealing your gut junctions naturally, and supporting your liver and nervous system."
    },
    {
      q: "What kind of dietary changes are involved? Are they too restrictive for life?",
      a: "Our approach is completely holistic. Food and nutrition are only one part of the reversal puzzle. The dietary adjustments are not meant to restrict you for life, but only temporarily until your digestive lining seals and your gut microbiome is resilient enough to digest these foods properly again."
    }
  ];

  return (
    <div className={`min-h-screen bg-[#FAF7F2] text-[#14261C] flex flex-col font-sans selection:bg-[#D8EEDF] selection:text-[#1A382B] ${showBottomBar ? "pb-24" : ""}`}>
      
      {/* Toast provider */}
      <Toaster position="top-right" theme="light" />

      {/* --- URGENCY ANNOUNCEMENT BANNER --- */}
      <div 
        data-testid="limited-time-banner"
        className="w-full bg-[#1A382B] text-[#FAF7F2] py-2.5 px-4 text-xs sm:text-sm font-medium border-b border-[#2E7D5B]/30 flex flex-col sm:flex-row justify-center items-center gap-2 relative z-50 text-center"
      >
        <span className="flex items-center gap-1 text-[#D8EEDF] animate-pulse">
          <Sparkle className="w-4 h-4 fill-current text-[#D8EEDF]" />
          <strong>LIMITED TIME OFFER:</strong>
        </span>
        <span>The Self-Heal Process will be withdrawn anytime without notice. Act now to save 50%!</span>
        <div className="flex items-center gap-1.5 bg-[#FAF7F2]/10 px-2 py-0.5 rounded text-[#FAF7F2] font-mono font-bold text-xs sm:text-sm">
          <Clock className="w-3.5 h-3.5" />
          <span data-testid="banner-countdown-timer">
            {timeLeft.hours.toString().padStart(2, "0")}h : {timeLeft.minutes.toString().padStart(2, "0")}m : {timeLeft.seconds.toString().padStart(2, "0")}s
          </span>
        </div>
      </div>

      {/* --- STICKY NAVIGATION BAR --- */}
      <nav 
        data-testid="sticky-navigation-bar"
        className={`w-full sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? "glass-nav py-3 shadow-md" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1A382B] flex items-center justify-center text-[#FAF7F2] font-serif font-bold text-lg border border-[#7E9F8E]">
              S
            </div>
            <div>
              <span className="hidden sm:block text-sm font-mono font-semibold tracking-wider text-[#7E9F8E] uppercase">
                learn.secondbraincoach.in
              </span>
              <span className="block text-base sm:text-lg font-bold tracking-tight text-[#1A382B]">
                Second Brain Coach
              </span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-[#475D52]">
            <a href="#about-philosophy" className="hover:text-[#1A382B] transition-colors">Philosophy</a>
            <a href="#assessment" className="hover:text-[#1A382B] transition-colors">Skin Assessment</a>
            <a href="#comparison" className="hover:text-[#1A382B] transition-colors">Self-Heal vs. Guided</a>
            <a href="#faq" className="hover:text-[#1A382B] transition-colors">FAQs</a>
          </div>

          {/* Nav Call to Action */}
          <div>
            <button
              data-testid="nav-claim-offer-button"
              onClick={() => handleOpenCheckout("standard")}
              className="bg-[#1A382B] hover:bg-[#264D3C] text-[#FAF7F2] px-4 sm:px-5 py-2 rounded-lg text-sm font-medium tracking-wide shadow-sm transition-all duration-200 transform active:scale-95 flex items-center gap-1.5"
            >
              Claim Offer
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <header className="relative py-12 sm:py-20 overflow-hidden bg-gradient-to-b from-[#FAF7F2] via-[#FAF7F2] to-[#F3EFEA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left side copy */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8">
              {/* Value prop badges */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="bg-[#D8EEDF] text-[#1A382B] px-3.5 py-1 rounded-full text-xs font-mono font-semibold tracking-wider uppercase border border-[#2E7D5B]/30 flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5 text-[#2E7D5B] fill-current" />
                  Self-Heal Edition Live
                </span>
                <span 
                  data-testid="hero-limited-access-badge"
                  className="bg-[#FEF3C7] text-[#92400E] px-3.5 py-1 rounded-full text-xs font-mono font-semibold tracking-wider uppercase border border-[#F59E0B]/30 flex items-center gap-1.5"
                >
                  <Clock className="w-3.5 h-3.5 text-[#92400E]" />
                  35-Day Digital Portal Access
                </span>
              </div>

              {/* Catchy Header */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#1A382B]">
                Your 21-Day <br className="hidden sm:inline" />
                <span className="text-[#2E7D5B] italic font-serif">Psoriasis Self-Heal</span> Process
              </h1>

              {/* Descriptive Subtext */}
              <p className="text-base sm:text-lg leading-relaxed text-[#475D52] max-w-xl">
                Please understand: <strong>21 days is not a full cure. It is the major, vital beginning</strong> and the single biggest step towards complete systemic reversal of psoriasis. Our clinically inspired self-guided protocol gets to the root cause of epidermal flares by sealing your gut lining and cooling systemic heat. 
              </p>

              {/* Bold Specific Copy */}
              <div className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-xl p-5 shadow-sm max-w-xl">
                <p className="text-sm font-semibold text-[#1A382B] mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4.5 h-4.5 text-[#92400E]" />
                  IMPORTANT OFFER NOTICE:
                </p>
                <p className="text-sm text-[#475D52] leading-relaxed">
                  This self-paced recovery protocol is identical in curriculum to our live, fully guided program but offered at a major discount. It is a <strong className="text-[#92400E]">LIMITED TIME OFFER and will be withdrawn anytime without notice</strong>. If you want to take matters into your own hands first, get access today!
                </p>
              </div>

              {/* Core Promises List */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-[#D8EEDF] rounded-full p-0.5 text-[#2E7D5B]">
                    <Check className="w-4 h-4 stroke-[3]" />
                  </div>
                  <p className="text-sm sm:text-base text-[#2C3E35]">
                    <strong>100% Same Curriculum:</strong> Get the exact diet protocols, shopping lists, and lifestyle resets used in our premium guided program.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-[#D8EEDF] rounded-full p-0.5 text-[#2E7D5B]">
                    <Check className="w-4 h-4 stroke-[3]" />
                  </div>
                  <p className="text-sm sm:text-base text-[#2C3E35]">
                    <strong>Completely Self-Paced:</strong> Learn and heal at your own comfort level with immediate access to all video modules.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1 bg-[#D8EEDF] rounded-full p-0.5 text-[#2E7D5B]">
                    <Check className="w-4 h-4 stroke-[3]" />
                  </div>
                  <p className="text-sm sm:text-base text-[#2C3E35]">
                    <strong>35-Day Digital Portal Access:</strong> Generous buffer access to download lifetime worksheets and solidify your daily gut habits.
                  </p>
                </div>
              </div>

              {/* Dual Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  data-testid="hero-primary-cta-button"
                  onClick={() => handleOpenCheckout()}
                  className="bg-[#1A382B] hover:bg-[#264D3C] text-[#FAF7F2] px-8 py-4 rounded-xl text-base sm:text-lg font-semibold tracking-wide shadow-lg hover:shadow-xl transition-all duration-200 transform active:scale-95 flex items-center justify-center gap-2 group"
                >
                  Claim 50% Off Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  data-testid="hero-secondary-cta-button"
                  href="#assessment"
                  className="bg-white hover:bg-[#F3EFEA] text-[#1A382B] border-2 border-[#E5DFD5] px-8 py-4 rounded-xl text-base sm:text-lg font-semibold tracking-wide shadow-sm hover:border-[#1A382B]/30 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  Take Skin Assessment
                </a>
              </div>
            </div>

            {/* Right side graphical card */}
            <div className="lg:col-span-5 relative">
              {/* Ambient circle glow */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#2E7D5B]/10 to-[#7E9F8E]/20 rounded-full blur-3xl -z-10" />

              {/* Main graphical Card */}
              <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4 shadow-xl relative overflow-hidden transition-all duration-300 hover:shadow-2xl">
                
                {/* Decorative border */}
                <div className="absolute top-0 inset-x-0 h-1.5 bg-[#2E7D5B]" />
                
                {/* Image */}
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-6 bg-[#FAF7F2] border border-[#E5DFD5]/50">
                  <img 
                    src="https://images.pexels.com/photos/25956384/pexels-photo-25956384.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                    alt="Sage leaf healing texture close up"
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-[#1A382B] text-xs font-mono font-bold uppercase tracking-wider py-1 px-3 rounded-lg border border-[#E5DFD5]">
                    Biophilic Medicine Path
                  </span>
                </div>

                {/* Micro Details */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-[#E5DFD5]/80 pb-3">
                    <span className="text-sm text-[#70857A] font-mono">Format</span>
                    <span className="text-sm font-semibold text-[#1A382B] flex items-center gap-1.5">
                      <Play className="w-4 h-4 text-[#2E7D5B]" />
                      Self-Guided Video Course
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-[#E5DFD5]/80 pb-3">
                    <span className="text-sm text-[#70857A] font-mono">Curriculum</span>
                    <span className="text-sm font-semibold text-[#1A382B]">
                      Identical to Live Program
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-1">
                    <span className="text-sm text-[#70857A] font-mono">Access Duration</span>
                    <span className="text-sm font-bold text-[#2E7D5B] bg-[#D8EEDF] px-2 py-0.5 rounded border border-[#2E7D5B]/20">
                      35 Days Digital Portal
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating Testimonial Snippet */}
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5DFD5] rounded-xl p-4 shadow-xl max-w-[260px] hidden sm:block animate-bounce-slow">
                <div className="flex items-center gap-1 text-[#F59E0B] mb-1.5">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                </div>
                <p className="text-xs text-[#2C3E35] leading-relaxed italic">
                  "After 10 years of heavy steroid creams, my scalp itch cleared up completely in Week 3 of the self-heal process!"
                </p>
                <span className="block text-[10px] font-mono font-semibold text-[#7E9F8E] mt-2 uppercase">
                  — Aarav S., Verified Student
                </span>
              </div>
            </div>

          </div>
        </div>
      </header>

      <main className="flex-grow">
        
        {/* --- SECTION 1: KEY VALUE PROPOSITIONS --- */}
        <section id="about-philosophy" className="py-16 sm:py-24 bg-white border-y border-[#E5DFD5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-[#4E7963]">
                The Self-Healing Methodology
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A382B]">
                Why Psoriasis Heals From the Inside Out
              </h2>
              <p className="text-base sm:text-lg text-[#475D52]">
                Steroid creams only thin the skin. Our science-backed process targets the intestinal lining and hepatic clearance to resolve underlying inflammation.
              </p>
            </div>

            {/* Bento Highlights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* Card 1 */}
              <div className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-2xl p-8 space-y-6 transition-card">
                <div className="w-12 h-12 rounded-xl bg-[#D8EEDF] flex items-center justify-center text-[#2E7D5B]">
                  <Check className="w-6 h-6 stroke-[3.5]" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-[#1A382B]">Gut Lining Tight Junctions</h3>
                  <p className="text-sm sm:text-base text-[#475D52] leading-relaxed">
                    Circulating bacterial toxins (LPS) pass through a weak gut barrier into the blood, activating systemic T-cells. We use mucilaginous herbs and targeted spore probiotics to seal these junctions.
                  </p>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-2xl p-8 space-y-6 transition-card">
                <div className="w-12 h-12 rounded-xl bg-[#D8EEDF] flex items-center justify-center text-[#2E7D5B]">
                  <Flame className="w-6 h-6" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-[#1A382B]">Nervous System Regulation</h3>
                  <p className="text-sm sm:text-base text-[#475D52] leading-relaxed">
                    Stress causes standard skin flares via cortisol surges. Our daily vagal nerve activation routines switch your neurology into restorative mode, calming immediate cellular itching.
                  </p>
                </div>
              </div>

              {/* Card 3 */}
              <div className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-2xl p-8 space-y-6 transition-card">
                <div className="w-12 h-12 rounded-xl bg-[#D8EEDF] flex items-center justify-center text-[#2E7D5B]">
                  <Layers className="w-6 h-6" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-[#1A382B]">Liver Conjugation Support</h3>
                  <p className="text-sm sm:text-base text-[#475D52] leading-relaxed">
                    When liver pathways are sluggish, the skin acts as an alternative excretion organ, pushing out toxins as dry plaques. We support Phase II methylation with organic clinical nutrition.
                  </p>
                </div>
              </div>

            </div>

            {/* Additional Philosophical Highlight Banner with Botanical Hands Image */}
            <div className="mt-16 bg-[#FAF7F2] border border-[#E5DFD5] rounded-3xl overflow-hidden shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-5 relative h-64 lg:h-auto min-h-[300px]">
                  <img 
                    src="https://images.pexels.com/photos/11812115/pexels-photo-11812115.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                    alt="Hands holding botanical leaves" 
                    className="w-full h-full object-cover absolute inset-0"
                  />
                </div>
                <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16 flex flex-col justify-center space-y-6">
                  <span className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-[#4E7963]">
                    The Power of Self-Pacing
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-semibold text-[#1A382B] tracking-tight">
                    You Are Your Own Best Therapist
                  </h3>
                  <p className="text-base text-[#475D52] leading-relaxed">
                    Nobody knows your body's daily patterns better than you do. The <strong>Self-Heal Process</strong> equips you with the exact biological frameworks and diagnostic matrices to track food reactions, adjust liver detox levels, and heal at your own comfort speed. Remember: 21 days is not full healing; it is the major beginning and the single biggest step towards complete reversal.
                  </p>
                  <p className="text-base text-[#475D52] leading-relaxed">
                    You get <strong>35 days of digital platform access</strong> to review modules, print out shopping checklists, and solidify your lifelong habits, giving you an ample buffer to kickstart your recovery.
                  </p>
                  <div className="pt-2">
                    <a
                      href="#assessment"
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1A382B] hover:text-[#2E7D5B] border-b border-[#1A382B] hover:border-[#2E7D5B] transition-all pb-0.5"
                    >
                      Start Free Skin Assessment
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>


        {/* --- SECTION 2: INTERACTIVE SELF-HEAL ASSESSMENT CALCULATOR --- */}
        <section id="assessment" className="py-16 sm:py-24 bg-[#F3EFEA]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-[#4E7963]">
                Personalized Diagnosis
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A382B]">
                Discover Your Custom Healing Path
              </h2>
              <p className="text-sm sm:text-base text-[#475D52]">
                Answer 3 quick biological questions to determine your inflammatory flare stage, timeline estimate, and receive an extra 10% custom discount!
              </p>
            </div>

            {/* Calculator Card Container */}
            <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-1 bg-[#2E7D5B]" />
              
              {/* Quiz Steps Indicator */}
              {assessmentStep <= 3 && (
                <div className="flex justify-between items-center mb-8 border-b border-[#E5DFD5]/80 pb-4">
                  <span className="text-xs font-mono text-[#70857A]">
                    STEP {assessmentStep} OF 3
                  </span>
                  <div className="flex gap-2">
                    <div className={`w-6 h-1 rounded-full ${assessmentStep >= 1 ? "bg-[#2E7D5B]" : "bg-[#E5DFD5]"}`} />
                    <div className={`w-6 h-1 rounded-full ${assessmentStep >= 2 ? "bg-[#2E7D5B]" : "bg-[#E5DFD5]"}`} />
                    <div className={`w-6 h-1 rounded-full ${assessmentStep >= 3 ? "bg-[#2E7D5B]" : "bg-[#E5DFD5]"}`} />
                  </div>
                </div>
              )}

              {/* Step 1: Skin Symptoms Type */}
              {assessmentStep === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-lg sm:text-xl font-semibold text-[#1A382B]">
                    1. Which description matches your active psoriasis symptoms?
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      data-testid="assessment-option-skin-plaque"
                      onClick={() => handleSelectOption("type", "plaque")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200 focus:ring-2 focus:ring-[#2E7D5B]"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">Plaque Psoriasis</strong>
                      <span className="text-xs text-[#70857A]">Dry, raised red patches covered with silvery scales</span>
                    </button>
                    <button
                      data-testid="assessment-option-skin-scalp"
                      onClick={() => handleSelectOption("type", "scalp")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200 focus:ring-2 focus:ring-[#2E7D5B]"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">Scalp Psoriasis</strong>
                      <span className="text-xs text-[#70857A]">Dry scales and intense flaking near hair roots</span>
                    </button>
                    <button
                      data-testid="assessment-option-skin-guttate"
                      onClick={() => handleSelectOption("type", "guttate")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200 focus:ring-2 focus:ring-[#2E7D5B]"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">Guttate or Inverse</strong>
                      <span className="text-xs text-[#70857A]">Small droplet dots or smooth, sore raw skin patches</span>
                    </button>
                    <button
                      data-testid="assessment-option-skin-erythrodermic"
                      onClick={() => handleSelectOption("type", "erythrodermic")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200 focus:ring-2 focus:ring-[#2E7D5B]"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">Erythrodermic / Severe</strong>
                      <span className="text-xs text-[#70857A]">Systemic redness, severe burning covering large areas</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Duration */}
              {assessmentStep === 2 && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-lg sm:text-xl font-semibold text-[#1A382B]">
                    2. How long have you experienced these continuous flareups?
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      data-testid="assessment-option-duration-under-6-months"
                      onClick={() => handleSelectOption("duration", "under-6-months")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">Under 6 Months</strong>
                      <span className="text-xs text-[#70857A]">Recent acute development, fast response probability</span>
                    </button>
                    <button
                      data-testid="assessment-option-duration-6-12-months"
                      onClick={() => handleSelectOption("duration", "6-12-months")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">6 to 12 Months</strong>
                      <span className="text-xs text-[#70857A]">Semi-chronic flare, likely linked to microbiome shifts</span>
                    </button>
                    <button
                      data-testid="assessment-option-duration-1-3-years"
                      onClick={() => handleSelectOption("duration", "1-3-years")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">1 to 3 Years</strong>
                      <span className="text-xs text-[#70857A]">Established chronic condition, needs systemic liver detox</span>
                    </button>
                    <button
                      data-testid="assessment-option-duration-3-years-plus"
                      onClick={() => handleSelectOption("duration", "3-years-plus")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-4 rounded-xl text-left transition-all duration-200"
                    >
                      <strong className="block text-sm text-[#1A382B] mb-1">Over 3 Years</strong>
                      <span className="text-xs text-[#70857A]">Long-term systemic dependence on creams/suppressants</span>
                    </button>
                  </div>
                  <button 
                    onClick={() => setAssessmentStep(1)}
                    className="text-xs text-[#70857A] underline hover:text-[#1A382B]"
                  >
                    ← Back to step 1
                  </button>
                </div>
              )}

              {/* Step 3: Intensity & Itch */}
              {assessmentStep === 3 && (
                <div className="space-y-6 animate-fadeIn">
                  <h3 className="text-lg sm:text-xl font-semibold text-[#1A382B]">
                    3. How would you rate the daily itch, soreness, and pain intensity?
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button
                      data-testid="assessment-option-itch-mild"
                      onClick={() => handleSelectOption("intensity", "mild")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-5 rounded-xl text-left transition-all duration-200"
                    >
                      <strong className="block text-base text-[#1A382B] mb-1">Mild</strong>
                      <span className="text-xs text-[#70857A]">Occasional itching, easily dry, manageable</span>
                    </button>
                    <button
                      data-testid="assessment-option-itch-moderate"
                      onClick={() => handleSelectOption("intensity", "moderate")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-5 rounded-xl text-left transition-all duration-200"
                    >
                      <strong className="block text-base text-[#1A382B] mb-1">Moderate</strong>
                      <span className="text-xs text-[#70857A]">Frequent skin peeling, disrupts sleep occasionally</span>
                    </button>
                    <button
                      data-testid="assessment-option-itch-intense"
                      onClick={() => handleSelectOption("intensity", "intense")}
                      className="border border-[#E5DFD5] hover:border-[#1A382B] hover:bg-[#FAF7F2] p-5 rounded-xl text-left transition-all duration-200 border-l-4 border-l-[#92400E]"
                    >
                      <strong className="block text-base text-[#92400E] mb-1">Intense / Severe</strong>
                      <span className="text-xs text-[#70857A]">Constant scaling, extreme stinging pain, major sleep loss</span>
                    </button>
                  </div>
                  <button 
                    onClick={() => setAssessmentStep(2)}
                    className="text-xs text-[#70857A] underline hover:text-[#1A382B]"
                  >
                    ← Back to step 2
                  </button>
                </div>
              )}

              {/* Step 4: Results */}
              {assessmentStep === 4 && calculatedPath && (
                <div 
                  data-testid="assessment-result-card"
                  className="space-y-6 sm:space-y-8 animate-fadeIn"
                >
                  <div className="p-6 bg-[#D8EEDF]/40 border border-[#2E7D5B]/30 rounded-2xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                      <div>
                        <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#2E7D5B] bg-[#D8EEDF] px-2.5 py-0.5 rounded border border-[#2E7D5B]/20">
                          Assessment Complete
                        </span>
                        <h4 className="text-xl sm:text-2xl font-semibold text-[#1A382B] mt-1.5">
                          Personalized Recovery Stage
                        </h4>
                      </div>
                      <div className="bg-white border border-[#2E7D5B]/20 rounded-xl px-4 py-2 text-center shadow-sm">
                        <span className="block text-[10px] font-mono text-[#70857A] uppercase">Clearance Probability</span>
                        <span className="block text-xl font-bold text-[#2E7D5B]">{calculatedPath.recoveryProbability}</span>
                      </div>
                    </div>

                    <div className="space-y-4 text-sm sm:text-base">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 border-b border-[#E5DFD5]/50 pb-3">
                        <span className="text-[#70857A] font-mono">Inflammatory Stage:</span>
                        <span className="sm:col-span-2 font-bold text-[#1A382B]">{calculatedPath.stage}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 border-b border-[#E5DFD5]/50 pb-3">
                        <span className="text-[#70857A] font-mono">Estimated Path Duration:</span>
                        <span className="sm:col-span-2 font-semibold text-[#1A382B]">{calculatedPath.durationNeeded}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <span className="text-[#70857A] font-mono">Clinical Focus:</span>
                        <span className="sm:col-span-2 text-[#475D52]">{calculatedPath.recommendation}</span>
                      </div>
                    </div>
                  </div>

                  {/* Coupon Unlock banner */}
                  <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-6 flex flex-col sm:flex-row justify-between items-center gap-6">
                    <div className="space-y-1.5 text-center sm:text-left">
                      <h5 className="text-sm font-mono font-bold text-[#92400E] flex items-center justify-center sm:justify-start gap-1">
                        <Percent className="w-4 h-4" />
                        60% OFF ENROLLMENT UNLOCKED
                      </h5>
                      <p className="text-sm text-[#92400E]/90">
                        We've generated the custom code <strong className="font-mono text-base bg-white/60 px-2 py-0.5 rounded border border-[#F59E0B]/20">HEALMYSKIN</strong> for you. Standard 50% discount upgraded to 60%!
                      </p>
                    </div>
                    <button
                      data-testid="assessment-submit-enroll-button"
                      onClick={handleUnlockCustomDiscount}
                      className="bg-[#1A382B] hover:bg-[#264D3C] text-[#FAF7F2] font-semibold text-sm px-6 py-3 rounded-xl shadow-md whitespace-nowrap transition-all duration-200"
                    >
                      Apply 60% Code & Enroll
                    </button>
                  </div>

                  <div className="flex justify-between items-center pt-2">
                    <button
                      onClick={handleResetAssessment}
                      className="text-xs text-[#70857A] hover:text-[#1A382B] underline"
                    >
                      ← Reset and take assessment again
                    </button>
                    <span className="text-xs text-[#70857A] italic">
                      *Note: Self-heal recommendations do not replace medical advice.
                    </span>
                  </div>
                </div>
              )}

            </div>
          </div>
        </section>





        {/* --- SECTION 4: COMPARISON MATRIX TABLE --- */}
        <section id="comparison" className="py-16 sm:py-24 bg-[#FAF7F2] border-t border-[#E5DFD5]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-[#4E7963]">
                Transparent Selection
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A382B]">
                Self-Heal vs. Fully Guided Program
              </h2>
              <p className="text-base text-[#475D52]">
                We believe in full clarity. Get the essential curriculum and digital guidelines, adapted for self-pacing.
              </p>
            </div>

            {/* Comparison Table Container */}
            <div 
              data-testid="program-comparison-matrix"
              className="bg-white border border-[#E5DFD5] rounded-2xl overflow-hidden shadow-md"
            >
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#1A382B] text-[#FAF7F2]">
                    <th className="p-3 sm:p-6 text-xs sm:text-sm font-mono uppercase tracking-wider">Features Included</th>
                    <th className="p-3 sm:p-6 text-center text-xs sm:text-sm font-mono uppercase tracking-wider bg-[#2E7D5B]">
                      Self-Heal Edition
                    </th>
                    <th className="p-3 sm:p-6 text-center text-xs sm:text-sm font-mono uppercase tracking-wider opacity-90">
                      Fully Guided Program
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DFD5] text-xs sm:text-sm text-[#14261C]">
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Core 21-Day Psoriasis Curriculum</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto stroke-[3]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Gut Sealing & Liver Detox Guides</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto stroke-[3]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">35-Day Digital Portal Access</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto stroke-[3]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Daily Somatic Stress Resets</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto stroke-[3]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Weekly Live Group QA Webinars</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#70857A]">
                      <X className="w-5 h-5 mx-auto stroke-[2]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Weekly Hackathon Calls</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#70857A]">
                      <X className="w-5 h-5 mx-auto stroke-[2]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Weekly Challenges</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#70857A]">
                      <X className="w-5 h-5 mx-auto stroke-[2]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="p-3 sm:p-6 font-medium">Full Course</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/20 text-[#70857A]">
                      <X className="w-5 h-5 mx-auto stroke-[2]" />
                    </td>
                    <td className="p-3 sm:p-6 text-center text-[#2E7D5B]">
                      <Check className="w-5 h-5 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-[#F3EFEA]/50">
                    <td className="p-3 sm:p-6 font-bold text-[#1A382B]">Program Value</td>
                    <td className="p-3 sm:p-6 text-center bg-[#D8EEDF]/40 font-bold text-[#1A382B]">
                      ₹2,999 + GST <span className="block text-[10px] text-[#70857A] font-normal">With code SELFHEAL50</span>
                    </td>
                    <td className="p-3 sm:p-6 text-center font-bold text-[#475D52]">
                      ₹29,999 <span className="block text-[10px] text-[#70857A] font-normal">Standard price</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-8 text-center bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-5 max-w-3xl mx-auto">
              <p className="text-xs sm:text-sm text-[#92400E] leading-relaxed">
                📢 <strong>WANT TO SELF-DO FIRST?</strong> This is the ultimate budget friendly way to test your body's sensitivity. If you decide to transition to the live coaching program later, we deduct 100% of your self-heal payment. <strong>LIMITED TIME OFFER!</strong>
              </p>
            </div>

          </div>
        </section>


        {/* --- SECTION 5: VERIFIED CLIENT TESTIMONIALS --- */}
        <section className="py-16 sm:py-24 bg-white border-y border-[#E5DFD5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-[#4E7963]">
                Real Results
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A382B]">
                Witness the Transformation
              </h2>
              <p className="text-base text-[#475D52]">
                Our self-paced graduates demonstrate that with correct diet metrics and stress rebalancing, rapid clearance is achievable.
              </p>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              
              {/* Testimonial 1 */}
              <div 
                data-testid="testimonial-card-0"
                className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row gap-6 relative"
              >
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden shrink-0 bg-[#FAF7F2] border border-[#E5DFD5]">
                  <img 
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=300&h=300" 
                    alt="Priya transformation photo" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3 flex-grow">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-mono text-[#7E9F8E] uppercase tracking-wider">Plaque & Scalp Clearance</span>
                    <div className="flex text-[#F59E0B]">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-[#1A382B]">Priya</h3>
                  <p className="text-sm text-[#475D52] leading-relaxed italic">
                    "Following the 21 days protocol this all was possible."
                  </p>
                  <span className="block text-xs font-mono font-semibold text-[#1A382B]">
                    Initial Clearance: 21 Days
                  </span>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div 
                data-testid="testimonial-card-1"
                className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row gap-6 relative"
              >
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden shrink-0 bg-[#FAF7F2] border border-[#E5DFD5]">
                  <img 
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=300&h=300" 
                    alt="Rajesh transformation photo" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3 flex-grow">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-mono text-[#7E9F8E] uppercase tracking-wider">Systemic Flare Reduction</span>
                    <div className="flex text-[#F59E0B]">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <Star className="w-3.5 h-3.5 fill-current" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-[#1A382B]">Rajesh</h3>
                  <p className="text-sm text-[#475D52] leading-relaxed italic">
                    "Following the 21 days protocol this all was possible."
                  </p>
                  <span className="block text-xs font-mono font-semibold text-[#1A382B]">
                    Initial Clearance: 28 Days
                  </span>
                </div>
              </div>

            </div>

          </div>
        </section>


        {/* --- SECTION 6: FAQS ACCORDION --- */}
        <section id="faq" className="py-16 sm:py-24 bg-[#F3EFEA]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Header */}
            <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
              <span className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-[#4E7963]">
                Got Questions?
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A382B]">
                Frequently Asked Inquiries
              </h2>
              <p className="text-base text-[#475D52]">
                Everything you need to know about access boundaries, upgrades, and healing safety.
              </p>
            </div>

            {/* Accordion List */}
            <div className="space-y-4">
              {faqItems.map((item, index) => {
                const isOpen = expandedFaq === index;
                return (
                  <div
                    key={index}
                    data-testid={`faq-accordion-item-${index}`}
                    className="bg-white border border-[#E5DFD5] rounded-xl overflow-hidden transition-all shadow-sm"
                  >
                    <button
                      onClick={() => setExpandedFaq(isOpen ? null : index)}
                      className="w-full text-left p-5 flex justify-between items-center gap-4 text-[#1A382B] hover:bg-[#FAF7F2] transition-colors"
                    >
                      <span className="text-base font-semibold leading-snug">{item.q}</span>
                      <div className="text-[#7E9F8E] shrink-0">
                        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="p-5 border-t border-[#E5DFD5]/80 bg-[#FAF7F2]/40 text-[#475D52] text-sm sm:text-base leading-relaxed animate-fadeIn">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </section>


        {/* --- FINAL CONVERSION CTA --- */}
        <section className="py-20 sm:py-28 bg-[#1A382B] text-[#FAF7F2] text-center relative overflow-hidden">
          {/* Ambient organic background accents */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-[#7E9F8E]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#2E7D5B]/20 rounded-full blur-3xl" />
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-8">
            <span className="text-xs uppercase tracking-[0.2em] font-mono font-bold text-[#7E9F8E]">
              Zero Risk • Complete Independence
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              Reclaim Vibrant, Healing Skin From Home
            </h2>
            <p className="text-base sm:text-lg text-[#D8EEDF] max-w-xl mx-auto leading-relaxed">
              Don't wait for the next flare-up. Secure your 35-day self-heal access window now. Save 50% immediately. 
            </p>
            
            {/* Promo timer block */}
            <div className="bg-[#FAF7F2]/10 border border-[#7E9F8E]/30 rounded-2xl p-6 max-w-md mx-auto space-y-3">
              <span className="block text-xs font-mono tracking-widest text-[#D8EEDF] uppercase">LIMITED TIME PROMOTION CLOSING</span>
              <div className="flex justify-center gap-6 text-white">
                <div>
                  <span className="block text-2xl font-bold font-mono">{timeLeft.hours.toString().padStart(2, "0")}</span>
                  <span className="text-[10px] uppercase font-mono text-[#7E9F8E]">Hours</span>
                </div>
                <span className="text-2xl font-bold animate-pulse">:</span>
                <div>
                  <span className="block text-2xl font-bold font-mono">{timeLeft.minutes.toString().padStart(2, "0")}</span>
                  <span className="text-[10px] uppercase font-mono text-[#7E9F8E]">Minutes</span>
                </div>
                <span className="text-2xl font-bold animate-pulse">:</span>
                <div>
                  <span className="block text-2xl font-bold font-mono">{timeLeft.seconds.toString().padStart(2, "0")}</span>
                  <span className="text-[10px] uppercase font-mono text-[#7E9F8E]">Seconds</span>
                </div>
              </div>
              <p className="text-xs text-[#FEF3C7] italic">
                *Notice: Coupon SELFHEAL50 expires shortly, and the Self-Heal program will be withdrawn.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
              <button
                data-testid="final-cta-enroll-standard-button"
                onClick={() => handleOpenCheckout("standard")}
                className="bg-white hover:bg-[#FAF7F2] text-[#1A382B] px-8 py-4 rounded-xl text-base sm:text-lg font-bold tracking-wide shadow-lg hover:shadow-xl transition-all duration-200 transform active:scale-95"
              >
                Enroll in Self Heal Process (₹2,999 + GST)
              </button>
              <button
                data-testid="final-cta-upgrade-premium-button"
                onClick={() => handleOpenCheckout("premium")}
                className="bg-[#2E7D5B] hover:bg-[#264D3C] text-white border border-[#7E9F8E]/30 px-8 py-4 rounded-xl text-base sm:text-lg font-bold tracking-wide shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Upgrade to Premium Bundle (₹4,499 + GST)
              </button>
            </div>
            
            <p className="text-xs text-[#70857A]">
              Secure Checkout • Holistic Curriculum • 35-Day Digital Portal Access
            </p>
          </div>
        </section>

      </main>

      {/* --- FOOTER --- */}
      <footer className="bg-[#FAF7F2] border-t border-[#E5DFD5] py-12 text-[#475D52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-6">
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1A382B] flex items-center justify-center text-[#FAF7F2] font-serif font-bold text-lg border border-[#7E9F8E]">
              S
            </div>
            <div>
              <span className="block text-xs font-mono uppercase tracking-wider text-[#7E9F8E]">Creator Site</span>
              <span className="text-[#1A382B] font-bold">learn.secondbraincoach.in</span>
            </div>
          </div>

          <div className="text-xs text-center sm:text-right space-y-1">
            <p>© 2026 Second Brain Coach. All rights reserved. Holistic wellness recommendations.</p>
            <div className="flex justify-center sm:justify-end gap-4 text-[#70857A]">
              <span className="hover:underline cursor-pointer">Terms of Service</span>
              <span className="hover:underline cursor-pointer">Privacy Policy</span>
            </div>
          </div>

        </div>
      </footer>


      {/* --- INTERACTIVE DEMO CHECKOUT MODAL --- */}
      {checkoutOpen && (
        <div 
          data-testid="interactive-checkout-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
        >
          <div className="bg-white border border-[#E5DFD5] rounded-3xl max-w-xl w-full p-6 sm:p-10 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            
            {/* Close button */}
            <button
              data-testid="checkout-modal-close-button"
              onClick={() => setCheckoutOpen(false)}
              className="absolute top-4 right-4 text-[#70857A] hover:text-[#1A382B] p-1.5 hover:bg-[#FAF7F2] rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {!orderConfirmed ? (
              // Phase 1: Checkout Form
              <div className="space-y-6">
                
                {/* Title */}
                <div className="text-center sm:text-left">
                  <span className="text-[10px] font-mono tracking-widest text-[#2E7D5B] bg-[#D8EEDF] px-2.5 py-0.5 rounded border border-[#2E7D5B]/20 uppercase">
                    Secure Checkout
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1A382B] mt-1.5">
                    Complete Your Self-Heal Access
                  </h3>
                  <p className="text-xs text-[#70857A]">
                    You are purchasing immediate 35-day digital portal credentials.
                  </p>
                </div>

                {/* Simulated payment alert */}
                <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-3 flex gap-2.5 items-start text-[#92400E] text-xs">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>
                    <strong>Secure Checkout Demonstration:</strong> Payments are processed via a highly secure, simulated environment. No actual funds are charged during this preview.
                  </p>
                </div>

                {/* Plan Selection cards with dynamic prices */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    data-testid="checkout-plan-standard-card"
                    onClick={() => setSelectedPlan("standard")}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      selectedPlan === "standard"
                        ? "border-[#1A382B] bg-[#FAF7F2] ring-2 ring-[#1A382B]"
                        : "border-[#E5DFD5] hover:bg-[#FAF7F2]/50"
                    }`}
                  >
                    <span className="block text-xs font-mono uppercase text-[#70857A] mb-1">Standard Service</span>
                    <strong className="block text-sm text-[#1A382B] leading-tight">Your 21-Day Psoriasis Self-Heal Process</strong>
                    <span className="text-xs text-[#2E7D5B] font-bold mt-1 block">
                      ₹{planDiscountedPrice("standard").toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + GST <span className="text-[10px] font-normal text-[#70857A] line-through">₹{originalPrices.standard.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </span>
                  </button>
                  <button
                    data-testid="checkout-plan-premium-card"
                    onClick={() => setSelectedPlan("premium")}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      selectedPlan === "premium"
                        ? "border-[#1A382B] bg-[#FAF7F2] ring-2 ring-[#1A382B]"
                        : "border-[#E5DFD5] hover:bg-[#FAF7F2]/50"
                    }`}
                  >
                    <span className="block text-xs font-mono uppercase text-[#70857A] mb-1">Premium Bundle</span>
                    <strong className="block text-sm text-[#1A382B] leading-tight">Your 21-Day Psoriasis Self-Heal Process with cook book</strong>
                    <span className="text-xs text-[#2E7D5B] font-bold mt-1 block">
                      ₹{planDiscountedPrice("premium").toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} + GST <span className="text-[10px] font-normal text-[#70857A] line-through">₹{originalPrices.premium.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </span>
                  </button>
                </div>

                {/* Coupon input with label below to avoid overlap */}
                <div className="space-y-2">
                  <form onSubmit={handleApplyPromo} className="flex gap-2">
                    <div className="relative flex-grow">
                      <input
                        data-testid="checkout-promo-code-input"
                        type="text"
                        placeholder="ENTER COUPON CODE"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="w-full bg-[#FAF7F2] border border-[#E5DFD5] text-xs sm:text-sm p-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1A382B] font-mono tracking-wider"
                      />
                    </div>
                    <button
                      data-testid="checkout-promo-apply-button"
                      type="submit"
                      className="bg-[#1A382B] hover:bg-[#264D3C] text-[#FAF7F2] px-4 py-3 rounded-lg text-xs font-bold font-mono tracking-wider uppercase whitespace-nowrap"
                    >
                      Apply
                    </button>
                  </form>
                  {appliedPromo && (
                    <div className="text-xs text-[#2E7D5B] font-mono flex items-center gap-1.5 bg-[#D8EEDF]/40 border border-[#2E7D5B]/20 p-2.5 rounded-lg">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                      <span>Coupon <strong>{appliedPromo}</strong> is active ({Math.round((1 - currentPrice / originalPrices[selectedPlan]) * 100)}% total savings)!</span>
                    </div>
                  )}
                </div>

                {/* Order Summary */}
                <div className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-xl p-4 text-xs sm:text-sm space-y-2">
                  <div className="flex justify-between items-center text-[#70857A]">
                    <span>Original Price:</span>
                    <span className="line-through">₹{originalPrices[selectedPlan].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-[#2E7D5B]">
                    <span>Coupon Discount:</span>
                    <span>-₹{discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-[#70857A]">
                    <span>Subtotal:</span>
                    <span data-testid="checkout-subtotal">₹{currentPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-[#70857A]">
                    <span>18% GST:</span>
                    <span data-testid="checkout-gst">₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm font-bold text-[#1A382B] border-t border-[#E5DFD5]/80 pt-2">
                    <span>Due Now (Incl. GST):</span>
                    <span data-testid="checkout-total">₹{totalDueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* Payment Form */}
                <form onSubmit={handleCheckoutSubmit} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-mono text-[#70857A] uppercase block">Cardholder Name</label>
                    <input
                      data-testid="checkout-card-name-input"
                      type="text"
                      required
                      placeholder="e.g. Aarav Sharma"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      className="w-full bg-[#FAF7F2] border border-[#E5DFD5] text-xs sm:text-sm p-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1A382B]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-mono text-[#70857A] uppercase block">Mock Credit Card Number</label>
                    <input
                      data-testid="checkout-card-number-input"
                      type="text"
                      required
                      placeholder="4111 •••• •••• 4111"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      className="w-full bg-[#FAF7F2] border border-[#E5DFD5] text-xs sm:text-sm p-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1A382B] font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-mono text-[#70857A] uppercase block">Expiry Date</label>
                      <input
                        data-testid="checkout-card-expiry-input"
                        type="text"
                        required
                        placeholder="MM / YY"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        className="w-full bg-[#FAF7F2] border border-[#E5DFD5] text-xs sm:text-sm p-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1A382B] text-center font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-mono text-[#70857A] uppercase block">Security CVV</label>
                      <input
                        data-testid="checkout-card-cvv-input"
                        type="password"
                        required
                        maxLength="3"
                        placeholder="•••"
                        value={cardCvv}
                        onChange={(e) => setCardCVV(e.target.value)}
                        className="w-full bg-[#FAF7F2] border border-[#E5DFD5] text-xs sm:text-sm p-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1A382B] text-center font-mono"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    data-testid="checkout-submit-order-button"
                    disabled={isSubmittingOrder}
                    className="w-full bg-[#2E7D5B] hover:bg-[#264D3C] text-white p-4 rounded-xl text-base font-bold shadow-lg shadow-[#2E7D5B]/10 hover:shadow-xl transition-all duration-200 transform active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isSubmittingOrder ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Simulating Payment Gateway...
                      </span>
                    ) : (
                      <>
                        Simulate Payment of ₹{totalDueAmount.toLocaleString('en-IN')}
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>

              </div>
            ) : (
              // Phase 2: Order Confirmed Screen
              <div className="text-center py-6 space-y-6 animate-scaleUp">
                <div className="w-16 h-16 rounded-full bg-[#D8EEDF] flex items-center justify-center text-[#2E7D5B] mx-auto border-2 border-[#2E7D5B]/20">
                  <CheckCircle2 className="w-10 h-10 stroke-[2.5]" />
                </div>

                <div className="space-y-2">
                  <span className="text-[10px] font-mono tracking-widest text-[#2E7D5B] bg-[#D8EEDF] px-2.5 py-0.5 rounded border border-[#2E7D5B]/20 uppercase font-bold">
                    Order Simulated Successfully
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#1A382B]">
                    Welcome to Your 21-Day Self-Heal Journey!
                  </h3>
                  <p className="text-sm text-[#475D52] max-w-md mx-auto">
                    Your simulated purchase has been registered for <strong>learn.secondbraincoach.in</strong>.
                  </p>
                </div>

                {/* Instructions Block */}
                <div className="bg-[#FAF7F2] border border-[#E5DFD5] rounded-2xl p-5 text-left text-xs sm:text-sm space-y-3.5 max-w-md mx-auto">
                  <h4 className="font-bold text-[#1A382B] flex items-center gap-2">
                    <Award className="w-4.5 h-4.5 text-[#2E7D5B]" />
                    HOW TO ACCESS YOUR PORTAL:
                  </h4>
                  <ol className="list-decimal pl-5 space-y-2 text-[#475D52]">
                    <li>We have sent a mock access confirmation email containing your client login link.</li>
                    <li>Use your email to access the Second Brain Coach digital recovery dashboard.</li>
                    <li>Your <strong>35 days of digital portal access</strong> begins today. Make sure to download recipes and checklists before your buffer expires!</li>
                  </ol>
                  <p className="text-[11px] text-[#70857A] italic text-center pt-2">
                    *Reminder: This site is a static preview.
                  </p>
                </div>

                <button
                  data-testid="checkout-return-to-landing-button"
                  onClick={() => setCheckoutOpen(false)}
                  className="bg-[#1A382B] hover:bg-[#264D3C] text-white px-8 py-3.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all text-sm"
                >
                  Return to Landing Page
                </button>
              </div>
            )}

          </div>
        </div>
      )}


      {/* --- STICKY BOTTOM CONVERSION ANCHOR BAR --- */}
      {showBottomBar && (
        <div 
          data-testid="sticky-bottom-action-bar"
          className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5DFD5] py-3.5 px-4 shadow-xl flex items-center justify-between animate-slideUp"
        >
          <div className="max-w-7xl mx-auto w-full flex flex-row items-center justify-between gap-4">
            
            {/* Left label */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex w-9 h-9 rounded-full bg-[#1A382B] items-center justify-center text-white text-xs font-serif font-bold">
                S
              </div>
              <div>
                <span className="block text-xs sm:text-sm font-bold text-[#1A382B] truncate max-w-[150px] sm:max-w-[280px]">
                  Psoriasis Self-Heal Process
                </span>
                <span className="block text-[10px] font-mono text-[#7E9F8E] uppercase tracking-wider">
                  Self-Do At Your Own Pace
                </span>
              </div>
            </div>

            {/* Price and CTA */}
            <div className="flex items-center gap-3 sm:gap-6">
              <div className="text-right">
                <span className="block text-[10px] font-mono text-[#92400E] uppercase tracking-widest font-semibold animate-pulse">
                  LIMITED OFFER
                </span>
                <span className="text-sm sm:text-base font-bold text-[#1A382B]">
                  ₹2,999 + GST <span className="text-xs font-normal text-[#70857A] line-through">₹5,999</span>
                </span>
              </div>
              <button
                data-testid="open-checkout-modal-button"
                onClick={() => handleOpenCheckout()}
                className="bg-[#2E7D5B] hover:bg-[#264D3C] text-white text-xs sm:text-sm px-4 sm:px-6 py-2.5 rounded-xl font-bold shadow-md transform active:scale-95 transition-all whitespace-nowrap"
              >
                Enroll Now
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default App;
